import { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { ADD_STORE_PRINTERS, DROP_STORE_PRINTERS, SET_DEFAULT_RECEIPT_PRINTER, UPDATE_RESTAURANT, UPDATE_STORE_PRINTER } from "src/constants/mutations";
import { GET_STATIONS_BY_STORE, GET_STORE_RECEIPT_SETTINGS } from "src/constants/queries";
import {
  RECEIPT_FONT_SIZE_OPTIONS,
  getSecondLanguageLabel,
  toReceiptLanguageValue,
  fromReceiptLanguageValue,
  type ReceiptLanguageSelection,
} from "src/constants/constants";
import { successToast, failureToast } from "src/utils/toasts";
import { BtnLoader } from "src/Loader";
import { useStorePrinters } from "src/features/MenuItems/hooks/useStorePrinters";
import { Station } from "src/interfaces/station.interface";
import CustomSelect from "src/components/common/CustomSelect";
import FormRowVertical from "src/components/common/FormRowVertical";

const fontOptionNumber = (v: unknown): number => (v === 1 || v === 2 || v === 3 ? (v as number) : 1);

type PrinterFormState = {
  correlationId?: string;
  assignedName: string;
  ipAddress: string;
  port: string;
  protocol: string;
  purpose: "RECEIPT" | "KITCHEN" | "ENTERPRISE_DISPATCH_SLIP";
  enabled: boolean;
};

const emptyPrinterForm: PrinterFormState = {
  assignedName: "",
  ipAddress: "",
  port: "9100",
  protocol: "RAW",
  purpose: "ENTERPRISE_DISPATCH_SLIP",
  enabled: true,
};

function ReceiptLanguageCheckboxes({
  label,
  value,
  onChange,
  hasSecondLang,
  secondLangLabel,
}: {
  label: string;
  value: ReceiptLanguageSelection;
  onChange: (v: ReceiptLanguageSelection) => void;
  hasSecondLang: boolean;
  secondLangLabel: string;
}) {
  const onEnglish = (checked: boolean) => {
    if (!checked && !value.second) return;
    onChange({ ...value, english: checked });
  };
  const onSecond = (checked: boolean) => {
    if (!checked && !value.english) return;
    onChange({ ...value, second: checked });
  };
  return (
    <div className="mt-3">
      <div className="text-[#9B9B9B] text-xs font-[300] mb-1">{label}</div>
      <div className="flex flex-wrap gap-x-6 gap-y-1">
        <label className={`flex items-center gap-2 ${!hasSecondLang ? "cursor-default" : "cursor-pointer"}`}>
          <input type="checkbox" checked={value.english} disabled={!hasSecondLang} onChange={(e) => onEnglish(e.target.checked)} className="receipt-lang-check w-6 h-6 min-w-[24px] min-h-[24px] rounded border border-[#9B9B9B] cursor-pointer disabled:cursor-default appearance-none" />
          <span className="text-sm">English</span>
        </label>
        {hasSecondLang && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={value.second} onChange={(e) => onSecond(e.target.checked)} className="receipt-lang-check w-6 h-6 min-w-[24px] min-h-[24px] rounded border border-[#9B9B9B] cursor-pointer appearance-none" />
            <span className="text-sm">{secondLangLabel}</span>
          </label>
        )}
      </div>
    </div>
  );
}

const PrinterSettings = () => {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const { storePrinters, fetchStorePrinters, isLoadingPrinters } = useStorePrinters();
  const [stations, setStations] = useState<Station[]>([]);
  const [isLoadingStations, setIsLoadingStations] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [kitchenLoading, setKitchenLoading] = useState(false);
  const [enterprisePrinterLoading, setEnterprisePrinterLoading] = useState(false);
  const [printerFormOpen, setPrinterFormOpen] = useState(false);
  const [printerFormLoading, setPrinterFormLoading] = useState(false);
  const [printerForm, setPrinterForm] = useState<PrinterFormState>(emptyPrinterForm);
  const [selectedReceiptPrinters, setSelectedReceiptPrinters] = useState<Map<string, string>>(new Map());
  const [selectedEnterprisePrinterId, setSelectedEnterprisePrinterId] = useState("");
  const [storeReceiptSettings, setStoreReceiptSettings] = useState<{
    receiptFontSizeRestaurantCopy?: number | null;
    receiptFontSizeCustomerCopy?: number | null;
    receiptFontSizeKitchenCopy?: number | null;
    secondLanguage?: string | null;
    receiptLanguageRestaurantCopy?: string | null;
    receiptLanguageCustomerCopy?: string | null;
    receiptLanguageKitchenCopy?: string | null;
    printReceiptAfterPayment?: boolean | null;
  } | null>(null);
  const [receiptFontSizeRestaurant, setReceiptFontSizeRestaurant] = useState<number>(1);
  const [receiptFontSizeCustomer, setReceiptFontSizeCustomer] = useState<number>(1);
  const [receiptFontSizeKitchen, setReceiptFontSizeKitchen] = useState<number>(1);
  const [receiptLangRestaurant, setReceiptLangRestaurant] = useState<ReceiptLanguageSelection>({ english: true, second: false });
  const [receiptLangCustomer, setReceiptLangCustomer] = useState<ReceiptLanguageSelection>({ english: true, second: false });
  const [receiptLangKitchen, setReceiptLangKitchen] = useState<ReceiptLanguageSelection>({ english: true, second: false });
  const [printReceiptAfterPayment, setPrintReceiptAfterPayment] = useState(false);
  const [section1Loading, setSection1Loading] = useState(false);

  const fetchStations = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    setIsLoadingStations(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_STATIONS_BY_STORE,
        variables: { storeCorrelationId: hasRestaurant.correlationId },
      });
      if (data?.errors?.length) throw data.errors;
      const fetchedStations = data?.data?.getStationsByStore || [];
      setStations(fetchedStations);
      const initial = new Map<string, string>();
      fetchedStations.forEach((s: Station) => {
        if (s.defaultReceiptPrinterCorrelationId) {
          initial.set(s.correlationId, s.defaultReceiptPrinterCorrelationId);
        }
      });
      setSelectedReceiptPrinters(initial);
    } catch (e) {
      console.error("Error fetching stations:", e);
      failureToast("Failed to load stations");
    } finally {
      setIsLoadingStations(false);
    }
  }, [axBe, hasRestaurant?.correlationId]);

  const fetchStoreReceiptSettings = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_STORE_RECEIPT_SETTINGS,
        variables: { retrieveStoresInput: { storeCorrelationIds: [hasRestaurant.correlationId] } },
      });
      const stores = data?.data?.stores || [];
      const s = stores[0] || null;
      setStoreReceiptSettings(
        s
          ? {
              receiptFontSizeRestaurantCopy: s.receiptFontSizeRestaurantCopy,
              receiptFontSizeCustomerCopy: s.receiptFontSizeCustomerCopy,
              receiptFontSizeKitchenCopy: s.receiptFontSizeKitchenCopy,
              secondLanguage: s.secondLanguage,
              receiptLanguageRestaurantCopy: s.receiptLanguageRestaurantCopy,
              receiptLanguageCustomerCopy: s.receiptLanguageCustomerCopy,
              receiptLanguageKitchenCopy: s.receiptLanguageKitchenCopy,
              printReceiptAfterPayment: s.printReceiptAfterPayment,
            }
          : null
      );
      if (s) {
        setReceiptFontSizeRestaurant(fontOptionNumber(s.receiptFontSizeRestaurantCopy));
        setReceiptFontSizeCustomer(fontOptionNumber(s.receiptFontSizeCustomerCopy));
        setReceiptFontSizeKitchen(fontOptionNumber(s.receiptFontSizeKitchenCopy));
        const hasSecond = !!(s.secondLanguage && s.secondLanguage !== "");
        setReceiptLangRestaurant(fromReceiptLanguageValue(s.receiptLanguageRestaurantCopy, hasSecond));
        setReceiptLangCustomer(fromReceiptLanguageValue(s.receiptLanguageCustomerCopy, hasSecond));
        setReceiptLangKitchen(fromReceiptLanguageValue(s.receiptLanguageKitchenCopy, hasSecond));
        setPrintReceiptAfterPayment(Boolean(s.printReceiptAfterPayment));
      }
    } catch (e) {
      console.error("Error fetching store receipt settings:", e);
    }
  }, [axBe, hasRestaurant?.correlationId]);

  useEffect(() => {
    fetchStorePrinters();
    fetchStoreReceiptSettings();
    fetchStations();
  }, [fetchStorePrinters, fetchStoreReceiptSettings, fetchStations]);

  const printerOptions = useMemo(
    () => [
      { value: "", label: "Select printer" },
      ...storePrinters.map((p) => ({
        value: p.correlationId,
        label: p.assignedName || p.model || "Unnamed Printer",
      })),
    ],
    [storePrinters]
  );

  const currentEnterprisePrinter = useMemo(
    () =>
      storePrinters.find(
        (printer) => printer.purpose === "ENTERPRISE_DISPATCH_SLIP" && printer.enabled !== false
      ) || null,
    [storePrinters]
  );

  useEffect(() => {
    setSelectedEnterprisePrinterId(currentEnterprisePrinter?.correlationId || "");
  }, [currentEnterprisePrinter?.correlationId]);

  const fontSizeOptions = RECEIPT_FONT_SIZE_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }));
  const printerPurposeOptions = [
    { value: "RECEIPT", label: "Receipt printer" },
    { value: "KITCHEN", label: "Kitchen printer" },
    { value: "ENTERPRISE_DISPATCH_SLIP", label: "Enterprise dispatch slip printer" },
  ];

  const hasSecondLang = !!(storeReceiptSettings?.secondLanguage && storeReceiptSettings.secondLanguage !== "");
  const secondLangLabel = useMemo(() => getSecondLanguageLabel(storeReceiptSettings?.secondLanguage), [storeReceiptSettings?.secondLanguage]);

  const handleReceiptPrinterSelect = (stationCorrelationId: string, printerCorrelationId: string) => {
    const next = new Map(selectedReceiptPrinters);
    if (printerCorrelationId) {
      next.set(stationCorrelationId, printerCorrelationId);
    } else {
      next.delete(stationCorrelationId);
    }
    setSelectedReceiptPrinters(next);
  };

  const stationPrinterSectionDirty = useMemo(() => {
    return stations.some((s) => {
      const current = s.defaultReceiptPrinterCorrelationId || "";
      const selected = selectedReceiptPrinters.get(s.correlationId) || "";
      return selected !== current;
    });
  }, [stations, selectedReceiptPrinters]);

  const saveStationPrinters = async () => {
    const toSave = stations.filter((s) => {
      const current = s.defaultReceiptPrinterCorrelationId || "";
      const selected = selectedReceiptPrinters.get(s.correlationId) || "";
      return selected !== current;
    });
    if (toSave.length === 0) return;
    setSection1Loading(true);
    try {
      const results = await Promise.all(
        toSave.map((s) =>
          axBe.post(GRAPHQL_ENDPOINT, {
            query: SET_DEFAULT_RECEIPT_PRINTER,
            variables: {
              stationCorrelationId: s.correlationId,
              storePrinterCorrelationId: selectedReceiptPrinters.get(s.correlationId) || null,
            },
          })
        )
      );
      const hasError = results.some((r) => r.data?.errors?.length);
      if (hasError) throw new Error("Update failed");
      successToast("Station printer configuration updated!");
      await fetchStations();
    } catch (err: any) {
      const msg = err?.message || err?.[0]?.message || "Failed to update station printers";
      failureToast(msg);
    } finally {
      setSection1Loading(false);
    }
  };

  const startAddPrinter = () => {
    setPrinterForm(emptyPrinterForm);
    setPrinterFormOpen(true);
  };

  const startEditPrinter = (printer: any) => {
    setPrinterForm({
      correlationId: printer.correlationId,
      assignedName: printer.assignedName || printer.model || "",
      ipAddress: printer.ipAddress || "",
      port: printer.port ? String(printer.port) : "9100",
      protocol: printer.protocol || "RAW",
      purpose: printer.purpose || (printer.isReceiptPrinter ? "RECEIPT" : "KITCHEN"),
      enabled: printer.enabled !== false,
    });
    setPrinterFormOpen(true);
  };

  const saveStorePrinter = async () => {
    if (!hasRestaurant?.correlationId) return;
    const name = printerForm.assignedName.trim();
    const ip = printerForm.ipAddress.trim();
    const port = Number(printerForm.port);
    if (!name || !ip || !Number.isInteger(port) || port < 1 || port > 65535) {
      failureToast("Enter printer name, IP address, and valid port.");
      return;
    }
    setPrinterFormLoading(true);
    try {
      const isEdit = Boolean(printerForm.correlationId);
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: isEdit ? UPDATE_STORE_PRINTER : ADD_STORE_PRINTERS,
        variables: isEdit
          ? {
              updateStorePrinterInput: {
                storeCorrelationId: hasRestaurant.correlationId,
                storePrinterCorrelationId: printerForm.correlationId,
                model: name,
                assignedName: name,
                ipAddress: ip,
                port,
                protocol: printerForm.protocol || "RAW",
                isReceiptPrinter: printerForm.purpose === "RECEIPT",
                purpose: printerForm.purpose,
                enabled: printerForm.enabled,
              },
            }
          : {
              addStorePrintersInput: {
                storeCorrelationId: hasRestaurant.correlationId,
                storePrinters: [
                  {
                    model: name,
                    assignedName: name,
                    ipAddress: ip,
                    port,
                    protocol: printerForm.protocol || "RAW",
                    isReceiptPrinter: printerForm.purpose === "RECEIPT",
                    purpose: printerForm.purpose,
                    enabled: printerForm.enabled,
                  },
                ],
              },
            },
      });
      if (data?.errors?.length) throw data.errors;
      successToast(isEdit ? "Printer updated!" : "Printer added!");
      setPrinterFormOpen(false);
      setPrinterForm(emptyPrinterForm);
      await fetchStorePrinters();
    } catch (err: any) {
      const msg = err?.message || err?.[0]?.message || "Failed to save printer";
      failureToast(msg);
    } finally {
      setPrinterFormLoading(false);
    }
  };

  const deleteStorePrinter = async (printerCorrelationId: string) => {
    if (!hasRestaurant?.correlationId) return;
    setPrinterFormLoading(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: DROP_STORE_PRINTERS,
        variables: {
          dropStorePrintersInput: {
            storeCorrelationId: hasRestaurant.correlationId,
            storePrinterCorrelationIds: [printerCorrelationId],
            forceDelete: true,
          },
        },
      });
      if (data?.errors?.length || data?.data?.dropStorePrinters !== true) throw data?.errors || new Error("Delete failed");
      successToast("Printer deleted!");
      await fetchStorePrinters();
    } catch (err: any) {
      const msg = err?.message || err?.[0]?.message || "Failed to delete printer";
      failureToast(msg);
    } finally {
      setPrinterFormLoading(false);
    }
  };

  const enterprisePrinterDirty = selectedEnterprisePrinterId !== (currentEnterprisePrinter?.correlationId || "");

  const saveEnterprisePrinter = async () => {
    if (!hasRestaurant?.correlationId || !enterprisePrinterDirty) return;
    setEnterprisePrinterLoading(true);
    try {
      const selectedPrinter = storePrinters.find((printer) => printer.correlationId === selectedEnterprisePrinterId);
      const updates = [];

      if (currentEnterprisePrinter && currentEnterprisePrinter.correlationId !== selectedEnterprisePrinterId) {
        updates.push(
          axBe.post(GRAPHQL_ENDPOINT, {
            query: UPDATE_STORE_PRINTER,
            variables: {
              updateStorePrinterInput: {
                storeCorrelationId: hasRestaurant.correlationId,
                storePrinterCorrelationId: currentEnterprisePrinter.correlationId,
                purpose: "ENTERPRISE_DISPATCH_SLIP",
                enabled: false,
              },
            },
          })
        );
      }

      if (selectedPrinter) {
        updates.push(
          axBe.post(GRAPHQL_ENDPOINT, {
            query: UPDATE_STORE_PRINTER,
            variables: {
              updateStorePrinterInput: {
                storeCorrelationId: hasRestaurant.correlationId,
                storePrinterCorrelationId: selectedPrinter.correlationId,
                purpose: "ENTERPRISE_DISPATCH_SLIP",
                enabled: true,
              },
            },
          })
        );
      }

      const results = await Promise.all(updates);
      const hasError = results.some((r) => r.data?.errors?.length);
      if (hasError) throw new Error("Update failed");
      successToast("Enterprise dispatch printer updated!");
      await fetchStorePrinters();
    } catch (err: any) {
      const msg = err?.message || err?.[0]?.message || "Failed to update enterprise dispatch printer";
      failureToast(msg);
    } finally {
      setEnterprisePrinterLoading(false);
    }
  };

  const receiptSectionDirty = (() => {
    const r = fontOptionNumber(storeReceiptSettings?.receiptFontSizeRestaurantCopy);
    const c = fontOptionNumber(storeReceiptSettings?.receiptFontSizeCustomerCopy);
    const hasSecond = hasSecondLang;
    const rLang = fromReceiptLanguageValue(storeReceiptSettings?.receiptLanguageRestaurantCopy, hasSecond);
    const cLang = fromReceiptLanguageValue(storeReceiptSettings?.receiptLanguageCustomerCopy, hasSecond);
    return (
      receiptFontSizeRestaurant !== r ||
      receiptFontSizeCustomer !== c ||
      receiptLangRestaurant.english !== rLang.english ||
      receiptLangRestaurant.second !== rLang.second ||
      receiptLangCustomer.english !== cLang.english ||
      receiptLangCustomer.second !== cLang.second ||
      printReceiptAfterPayment !== Boolean(storeReceiptSettings?.printReceiptAfterPayment)
    );
  })();

  const kitchenSectionDirty = (() => {
    const k = fontOptionNumber(storeReceiptSettings?.receiptFontSizeKitchenCopy);
    const hasSecond = hasSecondLang;
    const kLang = fromReceiptLanguageValue(storeReceiptSettings?.receiptLanguageKitchenCopy, hasSecond);
    return (
      receiptFontSizeKitchen !== k ||
      receiptLangKitchen.english !== kLang.english ||
      receiptLangKitchen.second !== kLang.second
    );
  })();

  const updateReceiptSettings = async (section: "receipt" | "kitchen") => {
    if (!hasRestaurant?.correlationId) return;
    if (section === "receipt") setReceiptLoading(true);
    else setKitchenLoading(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_RESTAURANT,
        variables: {
          updateStoreInput: {
            storeCorrelationId: hasRestaurant.correlationId,
            receiptFontSizeRestaurantCopy: receiptFontSizeRestaurant,
            receiptFontSizeCustomerCopy: receiptFontSizeCustomer,
            receiptFontSizeKitchenCopy: receiptFontSizeKitchen,
            receiptLanguageRestaurantCopy: toReceiptLanguageValue(receiptLangRestaurant),
            receiptLanguageCustomerCopy: toReceiptLanguageValue(receiptLangCustomer),
            receiptLanguageKitchenCopy: toReceiptLanguageValue(receiptLangKitchen),
            printReceiptAfterPayment,
          },
        },
      });
      if (data?.errors?.length) throw data.errors;
      // Update only this section's baseline so the other section's dirty state is unchanged
      setStoreReceiptSettings((prev) => {
        if (!prev) return prev;
        if (section === "receipt") {
          return {
            ...prev,
            receiptFontSizeRestaurantCopy: receiptFontSizeRestaurant,
            receiptFontSizeCustomerCopy: receiptFontSizeCustomer,
            receiptLanguageRestaurantCopy: toReceiptLanguageValue(receiptLangRestaurant),
            receiptLanguageCustomerCopy: toReceiptLanguageValue(receiptLangCustomer),
            printReceiptAfterPayment,
          };
        }
        return {
          ...prev,
          receiptFontSizeKitchenCopy: receiptFontSizeKitchen,
          receiptLanguageKitchenCopy: toReceiptLanguageValue(receiptLangKitchen),
        };
      });
      successToast(
        section === "kitchen" ? "Kitchen ticket printing configuration updated!" : "Receipt printing configuration updated!"
      );
    } catch (error) {
      console.error("Error updating receipt settings:", error);
    } finally {
      if (section === "receipt") setReceiptLoading(false);
      else setKitchenLoading(false);
    }
  };

  const cardClass = "bg-[#ffffff] rounded-[16px] px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)]";
  const titleClass = "text-[18px] font-semibold text-[#111827] tracking-[-0.02em]";
  const descClass = "text-[14px] text-[#6b7280]";

  if (isLoadingPrinters || isLoadingStations) {
    return (
      <div className="w-full max-w-[760px] flex justify-center items-center py-12">
        <BtnLoader />
      </div>
    );
  }

  return (
    <div className="w-full max-w-[760px] space-y-3">
      {/* Store printer management */}
      <div className={cardClass}>
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h2 className={`${titleClass} mb-2`}>Store printers</h2>
            <p className={`${descClass}`}>Add and manage printers available to this store.</p>
          </div>
          <button
            type="button"
            disabled={printerFormOpen || printerFormLoading}
            className={`px-4 py-2 rounded-xl bg-[#FD7657] text-white text-sm font-semibold ${printerFormOpen || printerFormLoading ? "opacity-50 cursor-not-allowed" : ""}`}
            onClick={startAddPrinter}
          >
            Add printer
          </button>
        </div>

        {printerFormOpen && (
          <div className="rounded-[12px] border border-[#e5e7eb] p-4 my-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormRowVertical label="Printer name">
                <input className="w-full rounded-xl border border-[#e5e7eb] px-3 py-2 text-sm" value={printerForm.assignedName} onChange={(e) => setPrinterForm((p) => ({ ...p, assignedName: e.target.value }))} />
              </FormRowVertical>
              <FormRowVertical label="IP address">
                <input className="w-full rounded-xl border border-[#e5e7eb] px-3 py-2 text-sm" value={printerForm.ipAddress} onChange={(e) => setPrinterForm((p) => ({ ...p, ipAddress: e.target.value }))} />
              </FormRowVertical>
              <FormRowVertical label="Port">
                <input className="w-full rounded-xl border border-[#e5e7eb] px-3 py-2 text-sm" value={printerForm.port} onChange={(e) => setPrinterForm((p) => ({ ...p, port: e.target.value.replace(/\D/g, "").slice(0, 5) }))} />
              </FormRowVertical>
              <FormRowVertical label="Purpose">
                <CustomSelect
                  options={printerPurposeOptions}
                  value={printerForm.purpose}
                  onChange={(v) => setPrinterForm((p) => ({ ...p, purpose: (v || "RECEIPT") as PrinterFormState["purpose"] }))}
                  placeholder="Purpose"
                  label="Purpose"
                />
              </FormRowVertical>
            </div>
            <label className="flex items-center gap-2 cursor-pointer mt-4">
              <input
                type="checkbox"
                checked={printerForm.enabled}
                onChange={(e) => setPrinterForm((p) => ({ ...p, enabled: e.target.checked }))}
                className="receipt-lang-check w-6 h-6 min-w-[24px] min-h-[24px] shrink-0 rounded border border-[#9B9B9B] cursor-pointer appearance-none"
              />
              <span className="text-sm">Enabled</span>
            </label>
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" className="px-4 py-2 rounded-xl border border-[#e5e7eb] text-sm font-semibold" onClick={() => setPrinterFormOpen(false)}>
                Cancel
              </button>
              <button type="button" className="px-4 py-2 rounded-xl bg-[#FD7657] text-white text-sm font-semibold" onClick={saveStorePrinter} disabled={printerFormLoading}>
                Save
              </button>
            </div>
          </div>
        )}

        {storePrinters.length === 0 ? (
          <p className="text-[14px] text-[#6b7280] mt-4">No printers found for this store.</p>
        ) : (
          <div className="divide-y divide-[#e5e7eb] mt-4">
            {storePrinters.map((printer) => (
              <div key={printer.correlationId} className="py-3 flex items-center justify-between gap-4">
                <div>
                  <div className="text-[14px] font-semibold text-[#111827]">{printer.assignedName || printer.model || "Unnamed printer"}</div>
                  <div className="text-[13px] text-[#6b7280]">
                    {printer.ipAddress || "No IP"}{printer.port ? `:${printer.port}` : ""} · {printer.purpose || (printer.isReceiptPrinter ? "RECEIPT" : "KITCHEN")} · {printer.enabled === false ? "Disabled" : "Enabled"}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="button" className="px-3 py-1.5 rounded-lg border border-[#e5e7eb] text-sm" onClick={() => startEditPrinter(printer)}>
                    Edit
                  </button>
                  <button type="button" className="px-3 py-1.5 rounded-lg border border-[#e5e7eb] text-sm text-red-600" onClick={() => deleteStorePrinter(printer.correlationId)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 1. Station Printer Configuration */}
      <div className={cardClass}>
        <h2 className={`${titleClass} mb-2`}>Station Printer Configuration</h2>
        <p className={`${descClass} mb-4`}>
          Assign a default receipt printer to each station. Receipts for that station will use this printer.
        </p>
        {stations.length === 0 ? (
          <p className="text-[14px] text-[#6b7280] mb-4">No stations found. Add stations under Terminal and Station.</p>
        ) : (
          <div className="space-y-4 mb-4">
            {stations.map((station) => {
              const currentPrinterId = station.defaultReceiptPrinterCorrelationId || "";
              const selectedPrinterId = selectedReceiptPrinters.get(station.correlationId) || currentPrinterId || "";
              return (
                <div key={station.correlationId} className="rounded-[12px] border border-[#e5e7eb] p-4">
                  <div className="flex items-center gap-4 mb-3">
                    <h3 className="text-[14px] font-medium text-[#111827]">{station.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${station.active ? "bg-green-100 text-green-800" : "bg-[#f3f4f6] text-[#6b7280]"}`}>
                      {station.active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="min-w-[200px]">
                    <label className="block text-[14px] font-medium text-[#4b5563] mb-1">Default receipt printer</label>
                    <CustomSelect
                      options={printerOptions}
                      value={selectedPrinterId}
                      onChange={(v) => handleReceiptPrinterSelect(station.correlationId, v || "")}
                      placeholder="Select printer"
                      label="Select printer"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex justify-end">
          {section1Loading ? (
            <BtnLoader />
          ) : (
            <button
              type="button"
              disabled={!stationPrinterSectionDirty}
              className={`px-5 py-2.5 rounded-xl bg-[#FD7657] text-white text-sm font-semibold hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20 focus:ring-offset-2 ${!stationPrinterSectionDirty ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={saveStationPrinters}
            >
              Update
            </button>
          )}
        </div>
      </div>

      {/* 2. Enterprise dispatch printer assignment */}
      <div className={cardClass}>
        <h2 className={`${titleClass} mb-2`}>Enterprise dispatch printer</h2>
        <p className={`${descClass} mb-4`}>
          Assign the store printer used by iPad stations for enterprise package and dispatch slips.
        </p>
        {storePrinters.length === 0 ? (
          <p className="text-[14px] text-[#6b7280] mb-4">No printers found for this store.</p>
        ) : (
          <div className="min-w-[200px] mb-4">
            <label className="block text-[14px] font-medium text-[#4b5563] mb-1">Enterprise dispatch printer</label>
            <CustomSelect
              options={printerOptions}
              value={selectedEnterprisePrinterId}
              onChange={(v) => setSelectedEnterprisePrinterId(v || "")}
              placeholder="Select printer"
              label="Enterprise dispatch printer"
            />
          </div>
        )}
        <div className="flex justify-end">
          {enterprisePrinterLoading ? (
            <BtnLoader />
          ) : (
            <button
              type="button"
              disabled={!enterprisePrinterDirty}
              className={`px-5 py-2.5 rounded-xl bg-[#FD7657] text-white text-sm font-semibold hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20 focus:ring-offset-2 ${!enterprisePrinterDirty ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={saveEnterprisePrinter}
            >
              Update
            </button>
          )}
        </div>
      </div>

      {/* 3. Receipt printing configuration */}
      <div className={cardClass}>
        <h2 className={`${titleClass} mb-2`}>Receipt printing configuration</h2>
        <p className={`${descClass} mb-4`}>
          Font size and language for restaurant and customer receipt copies.
        </p>
        <div className="mt-2">
          <p className="text-[14px] font-medium text-[#4b5563] mb-2">Receipt font size</p>
          <FormRowVertical label="Restaurant copy" className="mt-2">
            <CustomSelect options={fontSizeOptions} value={String(receiptFontSizeRestaurant)} onChange={(v) => setReceiptFontSizeRestaurant(v ? parseInt(v, 10) : 1)} placeholder="Normal" label="Restaurant copy" />
          </FormRowVertical>
          <FormRowVertical label="Customer copy" className="mt-2">
            <CustomSelect options={fontSizeOptions} value={String(receiptFontSizeCustomer)} onChange={(v) => setReceiptFontSizeCustomer(v ? parseInt(v, 10) : 1)} placeholder="Normal" label="Customer copy" />
          </FormRowVertical>
        </div>
        <div className="mt-6">
          <p className="text-[14px] font-medium text-[#4b5563] mb-2">Receipt language</p>
          <p className="text-[14px] text-[#6b7280] mb-2">Select one or both languages to print for each copy. The second language is set in the Language tab.</p>
          <p className="text-[13px] text-[#6b7280] mb-2">Receipts are automatically formatted for your selected language.</p>
          <ReceiptLanguageCheckboxes label="Restaurant copy" value={receiptLangRestaurant} onChange={setReceiptLangRestaurant} hasSecondLang={hasSecondLang} secondLangLabel={secondLangLabel} />
          <ReceiptLanguageCheckboxes label="Customer copy" value={receiptLangCustomer} onChange={setReceiptLangCustomer} hasSecondLang={hasSecondLang} secondLangLabel={secondLangLabel} />
        </div>
        <div className="mt-6">
          <p className="text-[14px] font-medium text-[#4b5563] mb-2">Receipt after payment</p>
          <p className="text-[14px] text-[#6b7280] mb-2">
            Choose whether receipts print automatically after a payment succeeds.
          </p>
          <p className="text-[13px] text-[#6b7280] mb-3">
            This uses the order&apos;s station printer, or the first active station if the order has no station.
          </p>
          <div className="mt-3">
            <div className="text-[#9B9B9B] text-xs font-[300] mb-1">Customer receipt</div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={printReceiptAfterPayment}
                onChange={(e) => setPrintReceiptAfterPayment(e.target.checked)}
                className="receipt-lang-check w-6 h-6 min-w-[24px] min-h-[24px] rounded border border-[#9B9B9B] cursor-pointer appearance-none"
              />
              <span className="text-sm">Print after payment</span>
            </label>
          </div>
        </div>
        <div className="flex justify-end mt-6">
          {receiptLoading ? (
            <BtnLoader />
          ) : (
            <button
              type="button"
              disabled={!receiptSectionDirty}
              className={`px-5 py-2.5 rounded-xl bg-[#FD7657] text-white text-sm font-semibold hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20 focus:ring-offset-2 ${!receiptSectionDirty ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={() => updateReceiptSettings("receipt")}
            >
              Update
            </button>
          )}
        </div>
      </div>

      {/* 4. Kitchen ticket printing configuration */}
      <div className={cardClass}>
        <h2 className={`${titleClass} mb-2`}>Kitchen ticket printing configuration</h2>
        <p className={`${descClass} mb-4`}>Font size and language for kitchen receipts.</p>
        <div className="mt-2">
          <p className="text-[14px] font-medium text-[#4b5563] mb-2">Kitchen ticket font size</p>
          <FormRowVertical label="Kitchen copy" className="mt-2">
            <CustomSelect
              options={fontSizeOptions}
              value={String(receiptFontSizeKitchen)}
              onChange={(v) => setReceiptFontSizeKitchen(v ? parseInt(v, 10) : 1)}
              placeholder="Normal"
              label="Kitchen copy font size"
            />
          </FormRowVertical>
        </div>
        <div className="mt-6">
          <p className="text-[14px] font-medium text-[#4b5563] mb-2">Kitchen ticket language</p>
          <p className="text-[14px] text-[#6b7280] mb-2">
            Select one or both languages to print for the kitchen copy. The second language is set in the Language tab.
          </p>
          <p className="text-[13px] text-[#6b7280] mb-2">Kitchen tickets are automatically formatted for your selected language.</p>
          <ReceiptLanguageCheckboxes
            label="Kitchen copy"
            value={receiptLangKitchen}
            onChange={setReceiptLangKitchen}
            hasSecondLang={hasSecondLang}
            secondLangLabel={secondLangLabel}
          />
        </div>
        <div className="flex justify-end mt-6">
          {kitchenLoading ? (
            <BtnLoader />
          ) : (
            <button
              type="button"
              disabled={!kitchenSectionDirty}
              className={`px-5 py-2.5 rounded-xl bg-[#FD7657] text-white text-sm font-semibold hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20 focus:ring-offset-2 ${!kitchenSectionDirty ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={() => updateReceiptSettings("kitchen")}
            >
              Update
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrinterSettings;
